<template>
  <div class="model-list-container">
    <div class="model-list-content">
      <div class="model-list-header">
        <h2>模型列表管理</h2>
        <p>管理AI模型，包括添加、编辑、删除模型</p>
      </div>
      
      <div class="model-actions">
        <el-button type="primary" @click="addModel">添加模型</el-button>
        <el-select v-model="filterCategory" placeholder="选择分类筛选" style="width: 200px; margin-left: 10px;" clearable @change="handleCategoryChange">
          <el-option label="全部" value=""></el-option>
          <el-option label="图片模型" value="image"></el-option>
          <el-option label="视频模型" value="video"></el-option>
          <el-option label="文案模型" value="text"></el-option>
          <el-option label="短剧模型" value="drama"></el-option>
        </el-select>
      </div>

      <div class="model-table">
        <el-table :data="paginatedModels" style="width: 100%" v-loading="loading">
          <el-table-column prop="id" label="ID" width="80"></el-table-column>
          <el-table-column prop="name" label="模型名称"></el-table-column>
          <el-table-column prop="identifier" label="标识符" width="150"></el-table-column>
          <el-table-column prop="category" label="分类" width="120">
            <template slot-scope="scope">
              {{ scope.row.category === 'image' ? '图片模型' : scope.row.category === 'video' ? '视频模型' : scope.row.category === 'text' ? '文案模型' : '短剧模型' }}
            </template>
          </el-table-column>
          <el-table-column prop="description" label="简介" min-width="200"></el-table-column>
          <el-table-column prop="model_type" label="类型" width="100">
            <template slot-scope="scope">
              {{ scope.row.model_type === 'free' ? '免费' : '付费' }}
            </template>
          </el-table-column>
          <el-table-column prop="api_key" label="API密钥" width="200">
            <template slot-scope="scope">
              <span v-if="!scope.row.showApiKey">••••••</span>
              <span v-else>{{ scope.row.api_key || '未设置' }}</span>
              <el-button size="mini" @click="toggleApiKey(scope.row)">
                {{ scope.row.showApiKey ? '隐藏' : '显示' }}
              </el-button>
            </template>
          </el-table-column>
          <el-table-column prop="status" label="状态" width="80">
            <template slot-scope="scope">
              <el-switch
                v-model="scope.row.status"
                @change="updateStatus(scope.row)"
              ></el-switch>
            </template>
          </el-table-column>
          <el-table-column prop="created_at" label="创建时间" width="180"></el-table-column>
          <el-table-column label="操作" width="150">
            <template slot-scope="scope">
              <el-button size="small" type="primary" @click="editModel(scope.row)">编辑</el-button>
              <el-button size="small" type="danger" @click="deleteModel(scope.row.id)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
        <el-pagination
          v-if="totalCount > 0"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[20, 50, 100, 200]"
          :page-size="20"
          layout="total, sizes, prev, pager, next, jumper"
          :total="totalCount"
          style="margin-top: 20px; text-align: right;"
        ></el-pagination>
      </div>
    </div>

    <!-- 添加/编辑模型对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="600px"
    >
      <el-form :model="form" :rules="rules" ref="form" label-width="100px">
        <el-form-item label="模型名称" prop="name">
          <el-input v-model="form.name" placeholder="请输入模型名称"></el-input>
        </el-form-item>
        <el-form-item label="标识符" prop="identifier">
          <el-input v-model="form.identifier" placeholder="请输入模型标识符，如 dall-e-3" :disabled="isEdit"></el-input>
          <div style="color: #909399; font-size: 12px; margin-top: 5px;">标识符用于代码中引用，创建后不能修改</div>
        </el-form-item>
        <el-form-item label="分类" prop="category">
          <el-select v-model="form.category" placeholder="选择模型分类" style="width: 100%">
            <el-option label="图片模型" value="image"></el-option>
            <el-option label="视频模型" value="video"></el-option>
            <el-option label="文案模型" value="text"></el-option>
            <el-option label="短剧模型" value="drama"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="类型" prop="model_type">
          <el-select v-model="form.model_type" placeholder="选择模型类型" style="width: 100%">
            <el-option label="免费模型" value="free"></el-option>
            <el-option label="付费模型" value="paid"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="简介" prop="description">
          <el-input
            v-model="form.description"
            type="textarea"
            :rows="3"
            placeholder="请输入模型简介（选填）"
          ></el-input>
        </el-form-item>
        <el-form-item label="API密钥" prop="api_key">
          <el-input v-model="form.api_key" placeholder="请输入API密钥（必填）" type="password" show-password></el-input>
        </el-form-item>
        <el-form-item label="API端点" prop="api_endpoint">
          <el-input v-model="form.api_endpoint" placeholder="请输入API端点地址（必填，如 https://dashscope.aliyuncs.com）"></el-input>
        </el-form-item>
        <el-form-item label="模型图片" prop="image_url">
          <div class="upload-area" @click="triggerImageUpload">
            <input 
              type="file" 
              ref="imageInput" 
              style="display: none" 
              accept="image/*" 
              @change="handleImageUpload"
            >
            <div v-if="form.image_url" class="uploaded-image">
              <img :src="form.image_url" alt="模型图片" class="preview-image">
              <button class="remove-btn" @click.stop="removeImage">×</button>
            </div>
            <div v-else class="upload-placeholder">
              <div class="upload-icon">📁</div>
              <p>点击上传图片</p>
              <p class="hint">支持 JPG、PNG 格式</p>
            </div>
          </div>
        </el-form-item>
        <el-form-item label="是否新模型" prop="is_new">
          <el-switch v-model="form.is_new" active-text="是" inactive-text="否"></el-switch>
        </el-form-item>
        <el-form-item label="是否热门" prop="is_hot">
          <el-switch v-model="form.is_hot" active-text="是" inactive-text="否"></el-switch>
        </el-form-item>
        <el-form-item label="折扣百分比" prop="discount">
          <el-input v-model="form.discount" placeholder="请输入折扣百分比（0-100，0表示无折扣）" type="number" min="0" max="100"></el-input>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-switch v-model="form.status" active-text="启用" inactive-text="禁用"></el-switch>
        </el-form-item>
        <el-form-item label="参数配置" prop="parameters">
          <el-input
            v-model="form.parameters"
            type="textarea"
            :rows="3"
            placeholder="请输入JSON格式的参数配置（选填）"
          ></el-input>
        </el-form-item>
        <el-form-item label="支持功能" prop="capabilities">
          <el-checkbox-group v-model="form.capabilities">
            <el-checkbox label="text_to_image">文生图</el-checkbox>
            <el-checkbox label="image_to_image">图生图</el-checkbox>
            <el-checkbox label="text_to_video">文生视频</el-checkbox>
            <el-checkbox label="image_to_video">图生视频</el-checkbox>
            <el-checkbox label="avatar_video">数字人</el-checkbox>
            <el-checkbox label="text_generation">文本生成</el-checkbox>
          </el-checkbox-group>
          <div style="color: #909399; font-size: 12px; margin-top: 5px;">勾选该模型支持的功能，用户选择时将自动过滤不匹配的模型</div>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveModel" :loading="saving">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import appCache from '../../utils/appCache'

export default {
  name: 'ModelList',
  data() {
    return {
      models: [],
      loading: false,
      saving: false,
      dialogVisible: false,
      dialogTitle: '',
      isEdit: false,
      filterCategory: '',
      currentPage: 1,
      pageSize: 20,
      form: {
        id: '',
        name: '',
        identifier: '',
        category: 'image',
        description: '',
        api_key: '',
        api_endpoint: '',
        image_url: '',
        is_new: false,
        is_hot: false,
        discount: 0,
        model_type: 'free',
        status: true,
        parameters: '',
        capabilities: []
      },
      rules: {
        name: [
          { required: true, message: '请输入模型名称', trigger: 'blur' }
        ],
        identifier: [
          { required: true, message: '请输入模型标识符', trigger: 'blur' },
          { pattern: /^[a-z0-9.-]+$/, message: '标识符只能包含小写字母、数字、横杠和点号', trigger: 'blur' }
        ],
        category: [
          { required: true, message: '请选择模型分类', trigger: 'change' }
        ],
        api_key: [
          { required: true, message: '请输入API密钥', trigger: 'blur' }
        ],
        api_endpoint: [
          { required: true, message: '请输入API端点地址', trigger: 'blur' }
        ]
      }
    }
  },
  created() {
    this.loadModels()
  },
  computed: {
    filteredModels() {
      if (!this.filterCategory) {
        return this.models
      }
      return this.models.filter(model => model.category === this.filterCategory)
    },
    paginatedModels() {
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.filteredModels.slice(start, end)
    },
    totalCount() {
      return this.filteredModels.length
    }
  },
  methods: {
    async loadModels() {
      this.loading = true
      try {
        const response = await axios.get('/api/admin/models')
        if (response.data.code === 200) {
          this.models = response.data.data.map(model => ({
            ...model,
            showApiKey: false
          }))
        }
      } catch (error) {
        console.error('加载模型列表失败:', error)
        this.$message.error('加载模型列表失败')
      } finally {
        this.loading = false
      }
    },
    addModel() {
      this.dialogTitle = '添加模型'
      this.isEdit = false
      this.form = {
        id: '',
        name: '',
        identifier: '',
        category: 'image',
        description: '',
        api_key: '',
        api_endpoint: '',
        image_url: '',
        is_new: false,
        is_hot: false,
        discount: 0,
        model_type: 'free',
        status: true,
        parameters: '',
        capabilities: []
      }
      this.dialogVisible = true
    },
    editModel(model) {
      this.dialogTitle = '编辑模型'
      this.isEdit = true
      this.form = {
        id: model.id,
        name: model.name,
        identifier: model.identifier,
        category: model.category,
        description: model.description || '',
        api_key: model.api_key || '',
        api_endpoint: model.api_endpoint || '',
        image_url: model.image_url || '',
        is_new: model.is_new || false,
        is_hot: model.is_hot || false,
        discount: model.discount || 0,
        model_type: model.model_type,
        status: model.status,
        parameters: model.parameters || '',
        capabilities: model.capabilities || []
      }
      this.dialogVisible = true
    },
    triggerImageUpload() {
      this.$refs.imageInput.click()
    },
    async handleImageUpload(event) {
      const file = event.target.files[0]
      if (!file) return
      
      if (!file.type.startsWith('image/')) {
        this.$message.error('请选择图片文件')
        return
      }
      
      if (file.size > 5 * 1024 * 1024) {
        this.$message.error('图片大小不能超过5MB')
        return
      }
      
      const formData = new FormData()
      formData.append('file', file)
      
      try {
        const response = await axios.post('/api/upload/image', formData)
        console.log('Upload response:', response.data)
        if (response.data.code === 200) {
          // 使用相对路径，让浏览器使用当前页面的域名和端口
          this.form.image_url = response.data.data.url
          console.log('Form image_url:', this.form.image_url)
          // 强制Vue更新DOM
          this.$forceUpdate()
        } else {
          this.$message.error(response.data.message || '上传图片失败')
        }
      } catch (error) {
        console.error('上传图片失败:', error)
        this.$message.error('上传图片失败')
      }
    },
    removeImage() {
      this.form.image_url = ''
    },
    async saveModel() {
      try {
        await this.$refs.form.validate()
      } catch (error) {
        return
      }

      this.saving = true
      try {
        if (this.form.id) {
          // 编辑模型
          const response = await axios.put(`/api/admin/models/${this.form.id}`, this.form)
          if (response.data.code === 200) {
            this.$message.success('模型编辑成功')
            await this.loadModels()
            // 刷新缓存
            await appCache.refreshCache()
          }
        } else {
          // 添加模型
          const response = await axios.post('/api/admin/models', this.form)
          if (response.data.code === 200) {
            this.$message.success('模型添加成功')
            await this.loadModels()
            // 刷新缓存
            await appCache.refreshCache()
          }
        }
        this.dialogVisible = false
      } catch (error) {
        console.error('保存模型失败:', error)
        const errorMsg = error.response?.data?.detail || '保存模型失败'
        this.$message.error(errorMsg)
      } finally {
        this.saving = false
      }
    },
    async deleteModel(id) {
      this.$confirm('确定要删除这个模型吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await axios.delete(`/api/admin/models/${id}`)
          if (response.data.code === 200) {
            this.$message.success('模型删除成功')
            await this.loadModels()
            // 刷新缓存
            await appCache.refreshCache()
          }
        } catch (error) {
          console.error('删除模型失败:', error)
          this.$message.error('删除模型失败')
        }
      }).catch(() => {
        this.$message.info('已取消删除')
      })
    },
    async updateStatus(model) {
      try {
        const response = await axios.put(`/api/admin/models/${model.id}`, model)
        if (response.data.code === 200) {
          this.$message.success('状态更新成功')
          // 刷新缓存
          await appCache.refreshCache()
        }
      } catch (error) {
        console.error('更新状态失败:', error)
        // 恢复原状态
        model.status = !model.status
        this.$message.error('更新状态失败')
      }
    },
    handleSizeChange(val) {
      this.pageSize = val
      this.currentPage = 1
    },
    handleCurrentChange(val) {
      this.currentPage = val
    },
    handleCategoryChange() {
      this.currentPage = 1
    },
    toggleApiKey(model) {
      model.showApiKey = !model.showApiKey
    }
  }
}
</script>

<style scoped>
.model-list-container {
  padding: 20px 0;
}

.model-list-content {
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  color: #333;
}

.model-list-header {
  margin-bottom: 30px;
}

.model-list-header h2 {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.model-list-header p {
  font-size: 14px;
  color: #666;
  margin: 0;
}

/* 图片上传样式 */
.upload-area {
  border: 2px dashed #d9d9d9;
  border-radius: 12px;
  width: 120px;
  height: 120px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.upload-area:hover {
  border-color: #00e6ac;
  background-color: rgba(0, 230, 172, 0.05);
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #999;
  width: 100%;
  height: 100%;
}

.upload-icon {
  font-size: 32px;
  margin-bottom: 8px;
}

.upload-placeholder p {
  margin: 2px 0;
  font-size: 12px;
}

.uploaded-image {
  position: relative;
  width: 100%;
  height: 100%;
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 10px;
}

.remove-btn {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #ff4d4f;
  color: white;
  border: none;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  z-index: 10;
}

.remove-btn:hover {
  background: #ff7875;
  transform: scale(1.1);
}

.model-actions {
  margin-bottom: 20px;
}

.model-table {
  margin-top: 20px;
}
</style>
